import React, { FC, useEffect, useMemo, useState } from 'react'
import {
  AppraisalAttachmentType,
  AppraisalType,
  AverageReport,
  CrewAppraisal,
  deleteCrewAppraisalAttachment,
} from '../core/_requests'
import { Company, CompanyAdmin, Crew, Rank, Vessel } from '../core/_models'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export type PendingAppraisalAttachment = {
  attachmentType: AppraisalAttachmentType
  file: File
}

type Props = {
  visible: boolean
  appraisal: CrewAppraisal | null
  isNew: boolean
  lockCrewSelection?: boolean
  isSuperAdmin?: boolean
  isSaving: boolean
  crewMembers: Crew[]
  ranks: Rank[]
  companyGroups?: CompanyAdmin[]
  subCompanies?: Company[]
  vessels?: Vessel[]
  onClose: () => void
  onChange: (next: CrewAppraisal) => void
  onSave: (pending: PendingAppraisalAttachment[]) => void
  onUploadExistingAttachment: (attachmentType: AppraisalAttachmentType, files: FileList | null) => void
  onAttachmentRemoved: (next: CrewAppraisal) => void
}

const APPRAISAL_TYPE_OPTIONS: { value: AppraisalType; label: string }[] = [
  { value: 'MID_TERM', label: 'Mid Term' },
  { value: 'HOD_SIGNOFF', label: 'HOD Signoff' },
  { value: 'SPECIAL_CASE', label: 'Special Case' },
]

const AVERAGE_OPTIONS: { value: AverageReport; label: string }[] = [
  { value: 'NTBE', label: 'NTBE (Not to be employed)' },
  { value: 'AVERAGE', label: 'Average' },
  { value: 'GOOD', label: 'Good' },
  { value: 'EXCELLENT', label: 'Excellent' },
]

const ATTACHMENT_TYPE_OPTIONS: { value: AppraisalAttachmentType; label: string }[] = [
  { value: 'APPRAISAL_RATINGS', label: 'Appraisal - Ratings' },
  { value: 'APPRAISAL_OFFICERS_ENGINEERS', label: 'Appraisal - Officers and Engineers' },
  { value: 'APPRAISAL_MASTER_CHIEF_ENGINEER', label: 'Appraisal - Master and Chief Engineer' },
]

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.55rem',
  color: '#3F4254',
  fontWeight: 600,
  fontSize: '0.95rem',
}

const CrewAppraisalModal: FC<Props> = ({
  visible,
  appraisal,
  isNew,
  lockCrewSelection = false,
  isSuperAdmin = false,
  isSaving,
  crewMembers,
  ranks,
  companyGroups = [],
  subCompanies = [],
  vessels = [],
  onClose,
  onChange,
  onSave,
  onUploadExistingAttachment,
  onAttachmentRemoved,
}) => {
  const [pendingAttachments, setPendingAttachments] = useState<PendingAppraisalAttachment[]>([])
  const [selectedAttachmentType, setSelectedAttachmentType] =
    useState<AppraisalAttachmentType>('APPRAISAL_RATINGS')
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  useEffect(() => {
    if (!visible) return
    setPendingAttachments([])
    setSelectedAttachmentType('APPRAISAL_RATINGS')
    setSelectedCompanyGroupId('')
    setSelectedCompanyAdminId('')
    setSelectedVesselId('')
  }, [visible, appraisal?.id])

  const rankNameMap = useMemo(() => {
    const map = new Map<number, string>()
    ranks.forEach((r) => map.set(r.id, r.rank))
    return map
  }, [ranks])

  const getSubCompanyGroupId = (company: Company): number | undefined => {
    return (company as any)?.cga?.id ?? (company as any)?.cgaid?.id ?? undefined
  }

  const getVesselCompanyAdminId = (vessel: Vessel): number | undefined => {
    return vessel.companyAdmin?.id ?? (vessel as any)?.companyAdminId ?? undefined
  }

  const getVesselCompanyGroupId = (vessel: Vessel): number | undefined => {
    return (
      (vessel as any)?.companyGroupAdminId ??
      vessel.companyGroupAdmin?.id ??
      (vessel.companyAdmin as any)?.cgaid?.id ??
      (vessel.companyAdmin as any)?.cga?.id
    )
  }

  const filteredSubCompanies = useMemo(() => {
    if (!selectedCompanyGroupId) return subCompanies
    return subCompanies.filter((company) => getSubCompanyGroupId(company) === Number(selectedCompanyGroupId))
  }, [subCompanies, selectedCompanyGroupId])

  const shouldShowSubCompanyFilter = Boolean(selectedCompanyGroupId) && filteredSubCompanies.length > 0

  const filteredVessels = useMemo(() => {
    if (selectedCompanyAdminId) {
      return vessels.filter((vessel) => getVesselCompanyAdminId(vessel) === Number(selectedCompanyAdminId))
    }
    if (selectedCompanyGroupId) {
      return vessels.filter((vessel) => getVesselCompanyGroupId(vessel) === Number(selectedCompanyGroupId))
    }
    return vessels
  }, [vessels, selectedCompanyAdminId, selectedCompanyGroupId])

  const filteredCrewMembers = useMemo(() => {
    return crewMembers.filter((crew) => {
      const matchesCompanyGroup =
        !selectedCompanyGroupId || crew.companyGroupAdminId === Number(selectedCompanyGroupId)
      const matchesCompanyAdmin =
        !selectedCompanyAdminId || crew.companyAdminId === Number(selectedCompanyAdminId)
      const matchesVessel =
        !selectedVesselId || (crew.vessel?.id || crew.vesselId) === Number(selectedVesselId)

      return matchesCompanyGroup && matchesCompanyAdmin && matchesVessel
    })
  }, [crewMembers, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId])

  useEffect(() => {
    if (!visible || !appraisal || !isNew) return
    if (!isSuperAdmin) return
    if (!appraisal.crewId) return

    const existsInFilteredList = filteredCrewMembers.some((crew) => crew.id === appraisal.crewId)
    if (existsInFilteredList) return

    onChange({
      ...appraisal,
      crewId: 0,
      crewName: '',
      rankId: null,
      rankName: null,
      vesselId: null,
      vesselName: null,
    })
  }, [
    visible,
    appraisal,
    isNew,
    isSuperAdmin,
    filteredCrewMembers,
    onChange,
  ])

  if (!visible || !appraisal) return null

  const resolvedAppraisalType = (appraisal.appraisalType || 'MID_TERM') as AppraisalType
  const resolvedAverageReport = (appraisal.averageReport || 'AVERAGE') as AverageReport

  const updateField = <K extends keyof CrewAppraisal>(field: K, value: CrewAppraisal[K]) => {
    onChange({
      ...appraisal,
      [field]: value,
    })
  }

  const handleCrewChange = (crewIdRaw: string) => {
    const crewId = Number(crewIdRaw)
    const selected = crewMembers.find((c) => c.id === crewId)
    const selectedAny = selected as any

    const resolvedRankId: number | null =
      selected?.rankId ??
      selectedAny?.rank?.id ??
      null

    const resolvedRankName =
      (resolvedRankId ? rankNameMap.get(resolvedRankId) : undefined) ??
      selectedAny?.rank?.rank ??
      ''

    onChange({
      ...appraisal,
      crewId: crewId || 0,
      crewName: selected?.name || '',
      rankId: resolvedRankId,
      rankName: resolvedRankName,
      vesselId: selected?.vessel?.id || selected?.vesselId || null,
      vesselName: selected?.vessel?.fleet_name || null,
    })
  }

  const MAX_FILE_SIZE_MB = 10
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

  const addPendingFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const validFiles: PendingAppraisalAttachment[] = []
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB`)
        continue
      }
      validFiles.push({
        attachmentType: selectedAttachmentType,
        file,
      })
    }
    if (validFiles.length > 0) {
      setPendingAttachments((prev) => [...prev, ...validFiles])
    }
  }

  const removePendingFile = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const removeSavedAttachment = async (attachmentId: number) => {
    if (!appraisal.id) return
    try {
      const next = await deleteCrewAppraisalAttachment(appraisal.id, attachmentId)
      onAttachmentRemoved(next)
      toast.success('Attachment removed')
    } catch (error) {
      console.error('Error removing attachment', error)
      showErrorToast(error, 'Failed to remove attachment', 'delete')
    }
  }

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{ width: '78vw', maxWidth: 1050, maxHeight: '88vh', overflow: 'auto' }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='m-0'>{isNew ? 'Add Crew Appraisal' : 'Edit Crew Appraisal'}</h5>
          <button type='button' className='btn-close' onClick={onClose} disabled={isSaving} />
        </div>

        <div className='p-5'>
          <div className='row g-4'>
            {isNew && !lockCrewSelection && isSuperAdmin && (
              <>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>
                    Company <span className='text-danger'>*</span>
                  </label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedCompanyGroupId}
                    onChange={(e) => {
                      const next = e.target.value ? Number(e.target.value) : ''
                      setSelectedCompanyGroupId(next)
                      setSelectedCompanyAdminId('')
                      setSelectedVesselId('')
                    }}
                  >
                    <option value=''>All Companies</option>
                    {companyGroups.map((companyGroup) => (
                      <option key={companyGroup.id} value={companyGroup.id}>
                        {companyGroup.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                {shouldShowSubCompanyFilter && (
                  <div className='col-md-4'>
                    <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Sub-Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyAdminId}
                      onChange={(e) => {
                        const next = e.target.value ? Number(e.target.value) : ''
                        setSelectedCompanyAdminId(next)
                        setSelectedVesselId('')
                      }}
                    >
                      <option value=''>All Sub-Companies</option>
                      {filteredSubCompanies.map((subCompany) => (
                        <option key={subCompany.id} value={subCompany.id}>
                          {subCompany.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

                <div className='col-md-4'>
                  <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>
                    Vessel <span className='text-danger'>*</span>
                  </label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId}
                    onChange={(e) => setSelectedVesselId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value=''>All Vessels</option>
                    {filteredVessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </>
            )}

            <div className='col-md-6'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>
                Crew Name <span className='text-danger'>*</span>
              </label>
              {isNew && !lockCrewSelection ? (
                <SearchableSelect
                  className='form-select'
                  value={appraisal.crewId || ''}
                  onChange={(e) => handleCrewChange(e.target.value)}
                >
                  <option value=''>Crew Name - Select Crew</option>
                  {(isSuperAdmin ? filteredCrewMembers : crewMembers).map((crew) => (
                    <option key={crew.id} value={crew.id}>
                      {crew.name}
                    </option>
                  ))}
                </SearchableSelect>
              ) : (
                <input className='form-control' value={appraisal.crewName || '-'} disabled />
              )}
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Rank</label>
              <input className='form-control' value={appraisal.rankName || '-'} disabled />
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>
                Appraisal Type <span className='text-danger'>*</span>
              </label>
              <SearchableSelect
                className='form-select'
                value={resolvedAppraisalType}
                onChange={(e) => updateField('appraisalType', e.target.value as AppraisalType)}
              >
                {APPRAISAL_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>
                Average Report <span className='text-danger'>*</span>
              </label>
              <SearchableSelect
                className='form-select'
                value={resolvedAverageReport}
                onChange={(e) => updateField('averageReport', e.target.value as AverageReport)}
              >
                {AVERAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SearchableSelect>
            </div>

            <div className='col-12'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Remarks</label>
              <textarea
                className='form-control'
                rows={3}
                value={appraisal.remarks || ''}
                onChange={(e) => updateField('remarks', e.target.value)}
                placeholder='Enter remarks'
              />
            </div>

            <div className='col-12'>
              <div className='border rounded p-4'>
                <h6 className='fw-bold mb-3'>Attachments</h6>

                <div className='row g-3 align-items-end'>
                  <div className='col-md-5'>
                    <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Attachment Type</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedAttachmentType}
                      onChange={(e) => setSelectedAttachmentType(e.target.value as AppraisalAttachmentType)}
                    >
                      {ATTACHMENT_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-md-7'>
                    {isNew ? (
                      <input
                        type='file'
                        className='form-control'
                        multiple
                        onChange={(e) => addPendingFiles(e.target.files)}
                      />
                    ) : (
                      <input
                        type='file'
                        className='form-control'
                        multiple
                        onChange={(e) => onUploadExistingAttachment(selectedAttachmentType, e.target.files)}
                      />
                    )}
                  </div>
                </div>

                {isNew && pendingAttachments.length > 0 && (
                  <div className='mt-4'>
                    <div className='fw-semibold mb-2'>Pending Uploads</div>
                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>File</th>
                            <th className='text-end'>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingAttachments.map((item, idx) => (
                            <tr key={`${item.file.name}-${idx}`}>
                              <td>{ATTACHMENT_TYPE_OPTIONS.find((x) => x.value === item.attachmentType)?.label}</td>
                              <td>{item.file.name}</td>
                              <td className='text-end'>
                                <button type='button' className='btn btn-sm btn-light-danger' onClick={() => removePendingFile(idx)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {!isNew && appraisal.attachments?.length > 0 && (
                  <div className='mt-4'>
                    <div className='fw-semibold mb-2'>Uploaded Attachments</div>
                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>File</th>
                            <th className='text-end'>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appraisal.attachments.map((item) => (
                            <tr key={item.id}>
                              <td>{ATTACHMENT_TYPE_OPTIONS.find((x) => x.value === item.attachmentType)?.label}</td>
                              <td>{item.fileName}</td>
                              <td className='text-end'>
                                <button type='button' className='btn btn-sm btn-light-danger' onClick={() => removeSavedAttachment(item.id)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4'>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button type='button' className='btn btn-primary' onClick={() => onSave(pendingAttachments)} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewAppraisalModal
